/**
 * Helpers da curva de retenção por origem de tráfego (aba Tráfego).
 *
 * O gráfico desenha uma <Area> por valor de UTM. Com dezenas de valores a
 * legenda vira um paredão que cobre o próprio gráfico, então quem decide
 * QUAIS séries entram é o TrafficTabContent. Este módulo concentra o ranking,
 * o rótulo curto e a paleta — sem React e sem estado.
 */

// Paleta das séries. São 8 cores porque 8 também é o teto de séries simultâneas
// (MAX_SERIES): com os dois números iguais, duas séries nunca dividem a mesma cor.
// Âmbar/laranja ficou de fora de propósito — #F59E0B é a cor da marcação de Pitch.
export const UTM_COLORS = [
  '#4A8CFF', // Azul principal
  '#10B981', // Verde
  '#8B5CF6', // Roxo
  '#EC4899', // Rosa
  '#06B6D4', // Cyan
  '#EF4444', // Vermelho
  '#6366F1', // Indigo
  '#84CC16', // Lima
];

// Quantas séries o gráfico escolhe sozinho e quantas o usuário pode chegar a ver.
export const AUTO_SERIES = 5;
export const MAX_SERIES = 8;

export interface RankedUtmSeries {
  value: string;
  label: string;
  volume: number;
  /** Linha sintética do backend ('Outros'): soma da cauda, não uma origem. */
  aggregated?: boolean;
}

interface RetentionLike {
  value: string;
  retention: { [time: string]: { count: number; percent: number } };
  aggregated?: boolean;
}

interface TrafficLike {
  value: string;
  plays?: number;
}

/**
 * Encurta o valor da UTM para caber numa legenda.
 *
 * O Meta Ads manda `Nome do conjunto|123456789012345` — o id só serve para
 * desempatar, então o rótulo fica com a parte legível e o valor cru continua
 * disponível no `title` de quem renderiza.
 */
export function prettyUtmLabel(value: string): string {
  if (!value) return '';
  const match = value.match(/^(.*)\|(\d{8,})$/);
  const short = match?.[1]?.trim();
  return short || value;
}

/**
 * Resolve rótulos curtos garantindo que dois itens do mesmo conjunto não fiquem
 * com o mesmo texto. "Conjunto 1" cinco vezes na legenda não ajuda ninguém, então
 * em caso de colisão anexamos os 4 últimos caracteres do valor cru.
 */
export function disambiguateLabels(values: string[]): Map<string, string> {
  const shortByValue = new Map<string, string>();
  const usageCount = new Map<string, number>();

  values.forEach((value) => {
    const short = prettyUtmLabel(value);
    shortByValue.set(value, short);
    usageCount.set(short, (usageCount.get(short) || 0) + 1);
  });

  const result = new Map<string, string>();
  shortByValue.forEach((short, value) => {
    if ((usageCount.get(short) || 0) > 1 && short !== value) {
      result.set(value, `${short} · ${value.slice(-4)}`);
    } else {
      result.set(value, short);
    }
  });

  return result;
}

/**
 * Volume de uma série, usado para ranquear.
 *
 * A fonte preferida é `plays` do bloco `traffic`, que é o mesmo número exibido
 * na tabela logo abaixo do gráfico. O fallback existe porque os dois blocos são
 * agregados de forma independente no backend: um valor pode aparecer na curva de
 * retenção sem ter linha correspondente em `traffic`. Nesse caso usamos o maior
 * `count` da própria curva, que é a quantidade de espectadores no início.
 */
export function getSeriesVolume(item: RetentionLike, playsByValue: Map<string, number>): number {
  const plays = playsByValue.get(item.value);
  if (typeof plays === 'number' && plays > 0) return plays;

  const counts = Object.values(item.retention || {}).map((point) => point?.count || 0);
  return counts.length ? Math.max(...counts) : 0;
}

/**
 * Ordena as séries da curva por volume decrescente.
 *
 * O backend devolve os grupos em ordem de varredura, que não é estável entre
 * requisições — daí o desempate explícito por nome.
 */
export function rankUtmSeries(
  retentionItems: RetentionLike[],
  trafficItems: TrafficLike[] = []
): RankedUtmSeries[] {
  if (!retentionItems?.length) return [];

  const playsByValue = new Map<string, number>();
  trafficItems?.forEach((source) => {
    if (source?.value) playsByValue.set(source.value, source.plays || 0);
  });

  const labels = disambiguateLabels(retentionItems.map((item) => item.value));

  return retentionItems
    .map((item) => ({
      value: item.value,
      label: labels.get(item.value) || item.value,
      volume: getSeriesVolume(item, playsByValue),
      aggregated: item.aggregated,
    }))
    .sort((a, b) => b.volume - a.volume || a.value.localeCompare(b.value, 'pt-BR'));
}
